import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    TrackerTypeBreadcrumbs,
    TrackerTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrackerTypeActions from "../../../actions/trackerTypes";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./TrackerTypePage.scss";

interface TrackerTypePageProps {
    form: Dtos.TrackerType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trackerType: Dtos.TrackerType;
    lookups: Dtos.Lookup[];
    loadingTrackerType: boolean;
    loadTrackerTypeFailure: boolean;
    loadTrackerTypeErrorCode: string;
    savingTrackerType: boolean;
    saveTrackerTypeSuccess: boolean;
    saveTrackerTypeFailure: boolean;
    creatingTrackerType: boolean;
    createTrackerTypeSuccess: boolean;
    validatingTrackerType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrackerType: TrackerTypeActions.ITrackerTypeClearActionCreator;
    loadTrackerType: TrackerTypeActions.ITrackerTypeLoadByIdActionCreator;
    saveTrackerType: TrackerTypeActions.ITrackerTypeSaveActionCreator;
    createTrackerType: TrackerTypeActions.ITrackerTypeCreateActionCreator;
    validateTrackerType: TrackerTypeActions.ITrackerTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trackerType";

const TrackerTypePage: FC<TrackerTypePageProps> = ({
    form,
    formState,
    formProperties,
    trackerType,
    lookups,
    loadingTrackerType,
    loadTrackerTypeFailure,
    loadTrackerTypeErrorCode,
    savingTrackerType,
    saveTrackerTypeSuccess,
    saveTrackerTypeFailure,
    creatingTrackerType,
    createTrackerTypeSuccess,
    validatingTrackerType,
    validationFailures,
    permissions,
    clearTrackerType,
    loadTrackerType,
    saveTrackerType,
    createTrackerType,
    validateTrackerType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trackerTypeId } = useParams<{ trackerTypeId: string }>();
    const trackerTypeIdNumber = parseInt(trackerTypeId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrackerType = {} as Dtos.TrackerType;

        loadForm(reduxFormName, Object.assign({ ...trackerType }, extraData));
    };


    // Load tracker type
    useEffect(() => {
        if (trackerTypeIdNumber && !trackerType && !loadingTrackerType) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(TrackerTypeBreadcrumbs(trackerTypeIdNumber));
            loadTrackerType(trackerTypeIdNumber);
        } else if (
            !trackerTypeIdNumber &&
            !creatingTrackerType &&
            !createTrackerTypeSuccess
        ) {
            createTrackerType();
            setBreadcrumbs(TrackerTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (trackerType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        trackerTypeIdNumber,
        trackerType,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        TrackerTypeBreadcrumbs,
        loadTrackerType,
        createTrackerType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrackerType,
        creatingTrackerType,
        createTrackerTypeSuccess,
        TrackerTypeCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrackerTypeFailure) {
            navigate(`/error/${loadTrackerTypeErrorCode}`);
        }
    }, [loadTrackerTypeFailure, loadTrackerTypeErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (trackerTypeIdNumber && trackerType?.value) {
            setBreadcrumbs(
                TrackerTypeBreadcrumbs(trackerTypeIdNumber, trackerType?.value)
            );
        }
    }, [
        trackerTypeIdNumber,
        trackerType,
        setBreadcrumbs,
        TrackerTypeBreadcrumbs,
    ]);

    // Reload tracker type after saving
    useEffect(() => {
        if (!savingTrackerType && saveTrackerTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Tracker type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/trackertypes";
                navigate(url);
            } else if (!trackerTypeIdNumber) {
                let url: string = `/settings/qa/trackertypes/${trackerType?.id}`;
                navigate(url);
                clearTrackerType();
            }
        }
    }, [
        savingTrackerType,
        saveTrackerTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrackerType && saveTrackerTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrackerType, saveTrackerTypeFailure]);

    // Cleanup effect to clear the tracker type on component unmount
    useEffect(() => {
        return () => {
            clearTrackerType();
            resetForm(reduxFormName);
        };
    }, [clearTrackerType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/trackertypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TrackerType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrackerType = {} as Dtos.TrackerType;

            await saveTrackerType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrackerType = {} as Dtos.TrackerType;

            if (form && hasBeenSubmitted && !validatingTrackerType) {
                await validateTrackerType(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TaskTypesUpdate,
                Dtos.Permission.TaskTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrackerType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trackerType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trackerType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "trackerType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trackerType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrackerType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <QALookupsLayout
            loading={loadingTrackerType}
            permission={Dtos.Permission.TaskTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrackerTypePageProps> => {
    let trackerType: Dtos.TrackerType | undefined = !(
        state.trackerTypes.formData instanceof Array
    )
        ? state.trackerTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trackerType: trackerType!,
        loadingTrackerType:
            state.trackerTypes.loadState &&
            state.trackerTypes.loadState.status == RequestState.Pending,
        loadTrackerTypeFailure:
            state.trackerTypes.loadState &&
            state.trackerTypes.loadState.status == RequestState.Failure,
        loadTrackerTypeErrorCode: state.trackerTypes.loadState.errorCode,
        savingTrackerType:
            state.trackerTypes.saveState &&
            state.trackerTypes.saveState.status == RequestState.Pending,
        saveTrackerTypeSuccess:
            state.trackerTypes.saveState &&
            state.trackerTypes.saveState.status == RequestState.Success,
        saveTrackerTypeFailure:
            state.trackerTypes.saveState &&
            state.trackerTypes.saveState.status == RequestState.Failure,
        creatingTrackerType:
            state.trackerTypes.createState &&
            state.trackerTypes.createState.status == RequestState.Pending,
        createTrackerTypeSuccess:
            state.trackerTypes.createState &&
            state.trackerTypes.createState.status == RequestState.Success,
        validatingTrackerType:
            state.trackerTypes.validateState &&
            state.trackerTypes.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.trackerType,
        formState: state.trackerTypes.formState,
        formProperties: state.trackerTypes.formProperties,
        validationFailures: trackerType
            ? state.trackerTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrackerTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrackerType: bindActionCreators(
            TrackerTypeActions.LoadTrackerTypeById,
            dispatch
        ),
        clearTrackerType: bindActionCreators(
            TrackerTypeActions.Clear,
            dispatch
        ),
        saveTrackerType: bindActionCreators(
            TrackerTypeActions.SaveTrackerType,
            dispatch
        ),
        createTrackerType: bindActionCreators(
            TrackerTypeActions.CreateTrackerType,
            dispatch
        ),
        validateTrackerType: bindActionCreators(
            TrackerTypeActions.ValidateTrackerType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrackerTypePage);
